export const STAR_COUNT = 10;

export interface StarSlot {
  id: number;
  cx: number;
  cy: number;
  size: number;
}

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Computes safe insets avoiding HUD controls and caption plaques.
 * Tall viewports (height >= 500): top 112, bottom 150, left/right 24.
 * Short viewports (height < 500): top 24, right 128 (HUD column), bottom 110, left 24.
 */
export function starSafeInsets(_width: number, height: number): Insets {
  if (height >= 500) {
    return { top: 112, right: 24, bottom: 150, left: 24 };
  }
  return { top: 24, right: 128, bottom: 110, left: 24 };
}

/**
 * Generates non-overlapping star slots partitioned into grid cells.
 * Safe rect = viewport minus safe insets.
 * Landscape-ish (width >= height): 5 cols x 2 rows.
 * Portrait (width < height): 2 cols x 5 rows.
 * Size = floor(min(cellW, cellH) * 0.9), capped at 150.
 * Center jitter per axis: (random() - 0.5) * ((cellDim - size) / 2).
 * Shuffles star ids 0..9 onto cells via Fisher-Yates, sorted by id on output.
 */
export function generateStarLayout(
  width: number,
  height: number,
  random: () => number = Math.random,
): StarSlot[] {
  const safeWInput = Number.isFinite(width) ? width : 0;
  const safeHInput = Number.isFinite(height) ? height : 0;

  const insets = starSafeInsets(safeWInput, safeHInput);
  const rawSafeW = safeWInput - insets.left - insets.right;
  const rawSafeH = safeHInput - insets.top - insets.bottom;

  const isDegenerate = rawSafeW <= 0 || rawSafeH <= 0;

  const safeLeft = isDegenerate ? 0 : insets.left;
  const safeTop = isDegenerate ? 0 : insets.top;
  const safeW = isDegenerate ? Math.max(1, safeWInput) : rawSafeW;
  const safeH = isDegenerate ? Math.max(1, safeHInput) : rawSafeH;
  const safeRight = safeLeft + safeW;
  const safeBottom = safeTop + safeH;

  const cols = safeWInput >= safeHInput ? 5 : 2;
  const rows = cols === 5 ? 2 : 5;

  const cellW = safeW / cols;
  const cellH = safeH / rows;

  let size = Math.floor(Math.min(cellW, cellH) * 0.9);
  size = Math.min(150, Math.max(1, Number.isFinite(size) ? size : 1));

  // Fisher-Yates shuffle of star ids 0..9 across cells
  const ids: number[] = Array.from({ length: STAR_COUNT }, (_, i) => i);
  for (let i = ids.length - 1; i > 0; i--) {
    const r = random();
    const safeR = Number.isFinite(r) ? Math.max(0, Math.min(0.999999999, r)) : 0;
    const j = Math.floor(safeR * (i + 1));
    const safeJ = Math.max(0, Math.min(i, j));
    const tmp = ids[i];
    ids[i] = ids[safeJ];
    ids[safeJ] = tmp;
  }

  const slackX = Math.max(0, cellW - size);
  const slackY = Math.max(0, cellH - size);

  const slots: StarSlot[] = [];

  for (let k = 0; k < STAR_COUNT; k++) {
    const r = Math.floor(k / cols);
    const c = k % cols;

    const cellLeft = safeLeft + c * cellW;
    const cellTop = safeTop + r * cellH;
    const cellCenterX = cellLeft + cellW / 2;
    const cellCenterY = cellTop + cellH / 2;

    const rx = random();
    const ry = random();
    const safeRx = Number.isFinite(rx) ? rx : 0.5;
    const safeRy = Number.isFinite(ry) ? ry : 0.5;

    // Jitter range per axis = (cellDim - size) / 2
    // With (random() - 0.5) * range, jitter spans [-range/2, +range/2],
    // guaranteeing stars stay within their cell with a >= 4px gap between neighbours.
    const jitterX = (safeRx - 0.5) * (slackX / 2);
    const jitterY = (safeRy - 0.5) * (slackY / 2);

    const rawCx = cellCenterX + jitterX;
    const rawCy = cellCenterY + jitterY;

    const minCx = safeLeft + size / 2;
    const maxCx = safeRight - size / 2;
    const cx = minCx <= maxCx ? Math.max(minCx, Math.min(maxCx, rawCx)) : (safeLeft + safeRight) / 2;

    const minCy = safeTop + size / 2;
    const maxCy = safeBottom - size / 2;
    const cy = minCy <= maxCy ? Math.max(minCy, Math.min(maxCy, rawCy)) : (safeTop + safeBottom) / 2;

    slots.push({
      id: ids[k],
      cx,
      cy,
      size,
    });
  }

  slots.sort((a, b) => a.id - b.id);
  return slots;
}

export type TapResult = { kind: 'counted'; count: number; complete: boolean } | { kind: 'duplicate' };

/**
 * Pure TypeScript domain model for a single 10-star counting round.
 * No DOM dependencies.
 */
export class CountingRound {
  private _epoch = 0;
  private _counted = new Set<number>();

  get epoch(): number {
    return this._epoch;
  }

  get count(): number {
    return this._counted.size;
  }

  get isComplete(): boolean {
    return this.count === STAR_COUNT;
  }

  isCounted(id: number): boolean {
    return this._counted.has(id);
  }

  countedIds(): number[] {
    return Array.from(this._counted).sort((a, b) => a - b);
  }

  tap(id: number): TapResult {
    if (typeof id !== 'number' || !Number.isInteger(id) || id < 0 || id >= STAR_COUNT) {
      throw new RangeError(
        `Star id must be an integer between 0 and ${STAR_COUNT - 1}, received ${id}`,
      );
    }

    if (this.isComplete || this._counted.has(id)) {
      return { kind: 'duplicate' };
    }

    this._counted.add(id);
    const count = this._counted.size;
    const complete = count === STAR_COUNT;
    return { kind: 'counted', count, complete };
  }

  reset(): void {
    this._counted.clear();
    this._epoch += 1;
  }
}
